/**
 * Shared MST Sports Tours header and footer for the parent letters — the same
 * layout as yourTourPdfBranding.js (angled left panel, crest + club name on
 * the right, dark footer band) in MST navy/amber with the white MST logo.
 */

const WHITE = [255, 255, 255];
const BLACK = [17, 24, 39];
const NAVY  = [13, 34, 64];    // #0D2240
const AMBER = [232, 160, 32];  // #E8A020

// The white MST logo is 1000 × 550 px.
const LOGO_RATIO = 550 / 1000;

function setColor(doc, rgb) { doc.setTextColor(rgb[0], rgb[1], rgb[2]); }
function setFill(doc, rgb)  { doc.setFillColor(rgb[0], rgb[1], rgb[2]); }
function setDraw(doc, rgb)  { doc.setDrawColor(rgb[0], rgb[1], rgb[2]); }

/**
 * Draws the MST branded header.
 * @param {object} doc         jsPDF instance
 * @param {string|null} logoDataUrl  White MST logo as data URL (PNG)
 * @param {string|null} crestDataUrl Club crest as data URL (optional)
 * @param {string|null} clubName     Club/school name (optional)
 * @returns {number} y where body content should start
 */
export function drawMstHeader(doc, logoDataUrl = null, crestDataUrl = null, clubName = null) {
  const pageW   = 210;
  const marginL = 18;
  const marginR = 18;
  const headerH = 46;

  setFill(doc, WHITE);
  doc.rect(0, 0, pageW, headerH, 'F');

  // Navy chevron (left panel) with an amber edge along its tip
  setFill(doc, AMBER);
  doc.lines(
    [[114.5, 0], [14, headerH / 2], [-14, headerH / 2], [-114.5, 0]],
    0, 0, [1, 1], 'F', true
  );
  setFill(doc, NAVY);
  doc.lines(
    [[112, 0], [14, headerH / 2], [-14, headerH / 2], [-112, 0]],
    0, 0, [1, 1], 'F', true
  );

  // ── Left panel — MST logo + company details ───────────────────────────────
  const leftLogoW = 30;
  const leftLogoH = leftLogoW * LOGO_RATIO;
  const leftLogoX = marginL - 2;
  const leftLogoY = (headerH - leftLogoH) / 2;
  const leftInfoX = leftLogoX + leftLogoW + 5;
  const leftInfoMaxW = 112 - (leftInfoX - marginL) - 4; // stay clear of the chevron tip

  drawLogo(doc, logoDataUrl, leftLogoX, leftLogoY, leftLogoW, leftLogoH);

  let ly = 11;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  setColor(doc, WHITE);
  doc.text('MST SPORTS TOURS LTD', leftInfoX, ly);
  ly += 3;

  setDraw(doc, AMBER);
  doc.setLineWidth(0.4);
  doc.line(leftInfoX, ly, marginL + 112 - 4, ly);
  ly += 4.5;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(6.5);
  setColor(doc, [190, 200, 215]);
  const addrLines = doc.splitTextToSize(
    '48 - 52 Penny Lane, Mossley Hill, Liverpool, L18 1DG', leftInfoMaxW
  );
  doc.text(addrLines, leftInfoX, ly);
  ly += addrLines.length * 3.8 + 2;

  doc.text('info@mstsportstours.com', leftInfoX, ly);
  ly += 3.8;
  doc.text('www.mstsportstours.com', leftInfoX, ly);

  // ── Right panel (white) — club crest + club name ───────────────────────────
  const rightX = 130; // start of white panel (after chevron tip + amber edge)
  const crestSize = 20;
  const crestX = pageW - marginR - crestSize;
  const crestY = (headerH - crestSize) / 2;
  const nameMaxW = crestX - rightX - 4;

  if (crestDataUrl) {
    try {
      doc.addImage(crestDataUrl, 'JPEG', crestX, crestY, crestSize, crestSize);
    } catch (_) {
      // ignore crest errors — not critical
    }
  }

  if (clubName) {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    setColor(doc, BLACK);
    const nameLines = doc.splitTextToSize(clubName, nameMaxW);
    const nameY = (headerH / 2) - ((nameLines.length - 1) * 2.5) + 1.5;
    doc.text(nameLines, rightX, nameY);
  }

  return headerH + 10; // y where body content should start
}

/**
 * Draws the MST branded footer.
 * @param {object} doc         jsPDF instance
 * @param {number} contentY    Current y after all body content
 * @param {string|null} logoDataUrl  White MST logo as data URL (PNG)
 */
export function drawMstFooter(doc, contentY, logoDataUrl) {
  const pageW   = 210;
  const pageH   = 297;
  const marginR = 18;
  const footerH = 30;
  const footerY = Math.max(contentY + 10, pageH - footerH);

  setFill(doc, NAVY);
  doc.rect(0, footerY, pageW, pageH - footerY, 'F');
  setFill(doc, AMBER);
  doc.rect(0, footerY, pageW, 1.2, 'F');

  // Decorative amber diagonals — far-left zone, clear of the text columns
  setDraw(doc, AMBER);
  doc.setLineWidth(1.8);
  doc.line(0,  pageH, 14, footerY + 2);
  doc.line(12, pageH, 26, footerY + 2);

  // Logo — far right
  const logoW = 30;
  const logoH = logoW * LOGO_RATIO;
  const logoX = pageW - marginR - logoW + 4;
  const logoYPos = footerY + (pageH - footerY - logoH) / 2;
  drawLogo(doc, logoDataUrl, logoX, logoYPos, logoW, logoH);

  // Contact row + company line, centred between the diagonals and the logo
  const midX = (30 + logoX - 4) / 2;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  setColor(doc, WHITE);
  const row1y = footerY + 12;
  doc.text('info@mstsportstours.com', midX - 30, row1y, { align: 'center' });
  doc.text('www.mstsportstours.com',  midX + 30, row1y, { align: 'center' });

  doc.setFontSize(6.5);
  setColor(doc, [160, 175, 195]);
  doc.text('MST Sports Tours Ltd · 48 - 52 Penny Lane, Mossley Hill, Liverpool, L18 1DG',
    midX, footerY + 21, { align: 'center' });
}

function drawLogo(doc, logoDataUrl, x, y, w, h) {
  if (logoDataUrl) {
    try {
      // Aliased + compressed so header and footer share one embedded copy.
      doc.addImage(logoDataUrl, 'PNG', x, y, w, h, 'mst-logo', 'FAST');
      return;
    } catch (_) { /* fall through to the text badge */ }
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  setColor(doc, WHITE);
  doc.text('MST', x + w / 2, y + h / 2 + 1, { align: 'center' });
  doc.setFontSize(5.5);
  setColor(doc, AMBER);
  doc.text('SPORTS TOURS', x + w / 2, y + h / 2 + 5, { align: 'center' });
}
